import { LitElement, html, css, PropertyValues, PropertyValueMap } from "lit";
import { property, customElement } from "lit/decorators.js";
import { LegSpeedSettings, VesselPlanningModel } from "../../../src/models/vessel-planning-model";
import "../../voyage-popup/src/voyage-popup";
import { VesselPerformanceModel } from "../../../src/models/vessel-performance-model";
import { getDefaultCbForIMO } from "./vessel-default-cb-values";
import { voyageCursorStyles } from "../../voyage-style/src/voyage-style";
import "@maersk-global/mds-components-core/mc-radio";
import { VoyageImoModel } from "../../../src/models/voyage-imo-model";

const MpsToKnots = (60 * 60) / 1852;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement("voy-vessel-chars-popup")
export class VoyVesselCharsPopup extends LitElement {
    @property({ type: Boolean }) shown: boolean = false;

    static get styles() {
        return [
            voyageCursorStyles,
            css`
        :host {
        }

        .container {
          display: flex;
          flex-direction: column;
          flex-wrap: nowrap;
          margin: 15px;
          /* grid-template-columns: 150px 150px; */
          align-items: left;
        }

        .warning-msg {
          color: #9f6000;
          background-color: #feefb3;
          border: 1px solid #9f6000;
          border-radius: 3px 3px 3px 3px;
          font-size: 14px;
          font-family: MaerskText;
          margin: 10px 0;
          padding: 10px;
          max-width: 350px;
        }

        label,
        span {
          color: var(--neutrals-grey-02, black);
          font-family: MaerskText;
          font-size: 14px;
        }

        input {
          text-align: right;
          height: 41px;
          box-sizing: border-box;
          border: 1px solid var(--neutrals-grey-06, grey);
          border-radius: 2.5px;
          color: var(--neutrals-grey-02, black);
          background-color: unset;
          font-size: 14px;
          font-family: MaerskText;
          max-width: 200px;
          margin-bottom: 10px;
        }

        input:invalid {
          border: 1px solid red;
        }

        input[type="number"]:focus-within {
          /* Drop Shadow Medium */
          box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06),
            0px 2px 4px rgba(20, 20, 20, 0.1);
          outline: none;
        }

        .ecdis-start-wp-inputs-container {
          display: flex;
          justify-content: left;
          gap: 20px;
        }

        .wp-input {
          display: flex;
          align-items: center;
          color: var(--neutrals-grey-02, black);
        }

        input[type="radio"] {
          vertical-align: middle;
          margin-top: 0;
          margin-right: 10px;
          margin-left: 0;
        }
        .ecdis-start-squats-inputs-container {
          display: flex;
          justify-content: left;
          gap: 20px;
        }
        mc-radio::part(label) {
            color: var(--neutrals-grey-02, black);
        }       
      `,
        ];
    }

    render() {
        const enableAcceptButton =
            this._height >= 0 &&
            this._minSpeed >= 0 &&
            this._maxSpeed >= 0 &&
            this._blockCoefficient >= 0 &&
            this._blockCoefficient <= 1 &&
            this._beamSize >= 0

        return html`
      <voyage-popup
        accept-button-text="Save"
        .shown=${this.shown}
        @accept=${this._save}
        @close=${() => this.dispatchEvent(new Event("close"))}
        header-text="Vessel Characteristics"
        .disableAcceptButton=${!enableAcceptButton}
        .rejectButtonText=${this._allowCancel ? "Cancel" : ""}
        .noCloseButton=${!this._allowCancel}
      >
        <div class="container">
          <div class="warning-msg">
            Speed values are only used for passage plans, not for calculations
            (for now).
          </div>

          <label for="height">Overall height (meters)</label>
          <input
            id="height"
            type="number"
            min="0"
            step="0.01"
            .value=${`${this._height !== -1 ? this._height : ""}`}
            required
            @input=${(e: InputEvent) => {
                this._height = (<HTMLInputElement>e.target).valueAsNumber;
            }}
          />

          <label for="minSpeed">Minimum speed (knots)</label>
          <input
            id="minSpeed"
            type="number"
            min="0"
            step="0.01"
            .value=${`${this._minSpeed !== -1 ? this._minSpeed : ""}`}
            required
            @input=${(e: InputEvent) => {
                this._minSpeed = (<HTMLInputElement>e.target).valueAsNumber;
            }}
          />

          <label for="maxSpeed">Maximum speed (knots)</label>
          <input
            id="maxSpeed"
            type="number"
            min="0"
            step="0.01"
            .value=${`${this._maxSpeed !== -1 ? this._maxSpeed : ""}`}
            required
            @input=${(e: InputEvent) => {
                this._maxSpeed = (<HTMLInputElement>e.target).valueAsNumber;
            }}
          />

          <label for="blockCoefficient">Block Coefficient</label>
          <input
            id="blockCoefficient"
            type="number"
            min="0"
            max="1"
            step="0.01"
            .value=${`${this._blockCoefficient !== -1 ? this._blockCoefficient : ""
            }`}
            required
            @input=${(e: InputEvent) => {
                this._blockCoefficient = (<HTMLInputElement>(
                    e.target
                )).valueAsNumber;
            }}
          />

          <label for="beamSize">Beam size (meters)</label>
          <input
            id="beamSize"
            type="number"
            min="0"
            step="0.01"
            .value=${`${this._beamSize !== -1 ? this._beamSize : ""
            }`}
            required
            @input=${(e: InputEvent) => {
                this._beamSize = (<HTMLInputElement>(
                    e.target
                )).valueAsNumber;
            }}
          />

          <span>Passage Plan - WP number start at:</span>
          <div class="ecdis-start-wp-inputs-container">
            <mc-radio
                fit="small"
                label="0"
                id="wp_index_0"
                name="wp_index"
                .checked=${this._ecdisWPStartIndex === 0}
                @change=${() => (this._ecdisWPStartIndex = 0)}
            >
            </mc-radio>
            <mc-radio
                fit="small"
                label="1"
                id="wp_index_1"
                name="wp_index"
                .checked=${this._ecdisWPStartIndex === 1}
                @change=${() => (this._ecdisWPStartIndex = 1)}
            >
            </mc-radio>

          </div>

          <span>Squat calculation based on:</span>
          <div class="ecdis-start-squats-inputs-container">
            <mc-radio
              label="Calcualted leg speed"
              id="squat_index_0"
              name="squat_index"
              fit="small"
              .checked=${this._legSpeedSetting === LegSpeedSettings.CALCULATED_SPEED}
              @change=${() => (this._legSpeedSetting = LegSpeedSettings.CALCULATED_SPEED)}
            >
            </mc-radio>

            <mc-radio
              label="Max leg speed"
              id="squat_index_1"
              name="squat_index"
              fit="small"
              .checked=${this._legSpeedSetting === LegSpeedSettings.MAX_SPEED}
              @change=${() => (this._legSpeedSetting = LegSpeedSettings.MAX_SPEED)}
            >
            </mc-radio>
          </div>
        </div>
      </voyage-popup>
    `;
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("shown") && this.shown) {
            this._onShown();
        }
    }
    firstUpdated() {
      this._getVesselsCharacterictics();
    }

    private async _getVesselsCharacterictics() {
      const chars = await VesselPlanningModel!.getVesselCharacteristics(); 

      if(!chars?.legSpeedSetting || !chars?.beamSize) {
        this.shown = true;
      }
    }
  
    @property({ attribute: false }) private _height: number = -1;
    @property({ attribute: false }) private _minSpeed: number = -1;
    @property({ attribute: false }) private _maxSpeed: number = -1;
    @property({ attribute: false }) private _blockCoefficient: number = -1;
    @property({ attribute: false }) private _beamSize: number = -1;
    @property({ attribute: false }) private _ecdisWPStartIndex: number = 1;
    @property({ attribute: false }) private _allowCancel: boolean = true;
    @property({ attribute: false }) private _legSpeedSetting: LegSpeedSettings | null = null;

    private async _onShown() {
        const charateristics =
            await VesselPlanningModel!.getVesselCharacteristics();
            const imoNo = !isVesselOnCloud ? (<any>window)["appenv"].vesselIMONumber : VoyageImoModel?.imo
        if (!charateristics) {
            this._height = -1;
            this._minSpeed = -1;
            this._maxSpeed = -1;
            this._blockCoefficient = -1;
            this._beamSize = -1;
            this._ecdisWPStartIndex = 1;
            this._allowCancel = false;
            this._legSpeedSetting = LegSpeedSettings.CALCULATED_SPEED;
            // Get block coefficient default from our table
            this._blockCoefficient = getDefaultCbForIMO(imoNo);

            // Use PAPIs maxSpeed as default value
            const configData = await VesselPerformanceModel!.getConfigurationData();
            if (configData && configData.maxSpeed) {
                this._maxSpeed = configData.maxSpeed;
            }
        } else {
            this._height = charateristics.height;
            this._maxSpeed = charateristics.maxSpeed * MpsToKnots;
            this._minSpeed = charateristics.minSpeed * MpsToKnots;
            this._blockCoefficient = charateristics.blockCoefficient;
            this._beamSize = charateristics.beamSize;
            this._ecdisWPStartIndex = charateristics.ecdisWPStartIndex;
            this._allowCancel = true;
            this._legSpeedSetting = charateristics.legSpeedSetting ?? null;
            // Get block coefficient default from our table
        }
    }

    private _save() {
        VesselPlanningModel!.setVesselCharacteristics({
            height: this._height,
            maxSpeed: this._maxSpeed / MpsToKnots,
            minSpeed: this._minSpeed / MpsToKnots,
            blockCoefficient: this._blockCoefficient,
            beamSize: this._beamSize,
            ecdisWPStartIndex: this._ecdisWPStartIndex,
            legSpeedSetting: this._legSpeedSetting ??  LegSpeedSettings.CALCULATED_SPEED, // default to calculated speed which we get from Hydra
        });
        this.shown = false;
        this.dispatchEvent(new Event("close"));
    }
}
